import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons'
import type { SubCompetency } from '../../types/GrowthWheelManagement'
import {
  useDeleteEditorSubCompetencyMutation,
  useReorderEditorSubCompetenciesMutation,
  useUpdateEditorSubCompetencyMutation,
} from '../../redux/growth-wheel.endpoints'
import { getLevelCompletionStatus } from './wheelManagementUtils'
import { RichTextView } from './RichTextView'
import { WheelManagementDragHandle } from './WheelManagementDragHandle'
import { WheelManagementActionsConfirmModal } from './WheelManagementActionsConfirmModal'
import {
  EditSubCompetencyPanel,
  subCompetencyEditableContentEqual,
} from './EditSubCompetencyPanel'
import { buildSubCompetencyEditWarningModal } from './wheelManagementWarningModalService'
import {
  getGwWmActiveDrag,
  getGwWmDragData,
  setGwWmActiveDrag,
  setGwWmDragData,
  setGwWmDragPreviewFromNode,
} from './wheelManagementDnD'
import type { RootState } from '../../redux/store'

type Props = {
  growthWheelId: number
  subCompetency: SubCompetency
  competencyId: string
  competencyIndex: number
  subIndex: number
  siblingSubCompetencies: SubCompetency[]
  roleName?: string
  parentCompetencyName: string
  onCompetencyReorderDrag: (fromIndex: number, toIndex: number) => void
  subDropTargetIndex: number | null
  onSubDropTargetChange: (index: number | null) => void
}

export function SubCompetencyRow({
  growthWheelId,
  subCompetency,
  competencyId,
  competencyIndex,
  subIndex,
  siblingSubCompetencies,
  roleName,
  parentCompetencyName,
  onCompetencyReorderDrag,
  subDropTargetIndex,
  onSubDropTargetChange,
}: Props) {
  const isReadOnly = useSelector(
    (state: RootState) => state.growthWheel.isReadOnly,
  )
  const rowRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editingSubCompetency, setEditingSubCompetency] =
    useState<SubCompetency | null>(null)
  const [editedSubCompetency, setEditedSubCompetency] =
    useState<SubCompetency | null>(null)
  const [saveWarningOpen, setSaveWarningOpen] = useState(false)

  const [deleteSubCompetency] = useDeleteEditorSubCompetencyMutation()
  const [reorderSubCompetencies] = useReorderEditorSubCompetenciesMutation()
  const [updateSubCompetency] = useUpdateEditorSubCompetencyMutation()

  useEffect(() => {
    if (!isReadOnly) return
    setEditingSubCompetency(null)
    setEditedSubCompetency(null)
    setSaveWarningOpen(false)
  }, [isReadOnly])

  const levelStatus = getLevelCompletionStatus(subCompetency)
  const isComplete = levelStatus.defined === levelStatus.total
  const isDropTarget = subDropTargetIndex === subIndex && !isDragging

  const persistSubOrder = async (fromIndex: number, toIndex: number) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= siblingSubCompetencies.length ||
      toIndex >= siblingSubCompetencies.length
    ) {
      return
    }
    const next = [...siblingSubCompetencies]
    const [item] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, item)
    await reorderSubCompetencies({
      growthWheelId,
      parentCompetencyId: Number(competencyId),
      orderedSubCompetencyIds: next.map(c => Number(c.id)),
    }).unwrap()
  }

  const confirmDelete = async () => {
    await deleteSubCompetency({
      growthWheelId,
      subCompetencyId: Number(subCompetency.id),
    }).unwrap()
    setDeleteModalOpen(false)
  }

  const startEdit = () => {
    if (isReadOnly) return
    setEditingSubCompetency(subCompetency)
    setEditedSubCompetency({ ...subCompetency })
  }

  const closeEditPanel = () => {
    setEditingSubCompetency(null)
    setEditedSubCompetency(null)
    setSaveWarningOpen(false)
  }

  const requestSaveEdit = () => {
    if (
      !editedSubCompetency ||
      !editingSubCompetency ||
      !editedSubCompetency.name.trim() ||
      subCompetencyEditableContentEqual(
        editedSubCompetency,
        editingSubCompetency,
      )
    ) {
      return
    }
    setSaveWarningOpen(true)
  }

  const confirmSaveEdit = async () => {
    if (!editedSubCompetency) return
    await updateSubCompetency({
      growthWheelId,
      subCompetencyId: Number(editedSubCompetency.id),
      body: {
        name: editedSubCompetency.name,
        description: editedSubCompetency.description,
        levelDescriptions: editedSubCompetency.levelDescriptions,
      },
    }).unwrap()
    closeEditPanel()
  }

  const saveWarningContent = editedSubCompetency
    ? buildSubCompetencyEditWarningModal(editedSubCompetency.name)
    : null

  const handleSubDragStart = (e: React.DragEvent<HTMLSpanElement>) => {
    if (isReadOnly) return
    setGwWmDragData(e.dataTransfer, {
      kind: 'subCompetency',
      competencyId,
      fromIndex: subIndex,
    })
    setGwWmDragPreviewFromNode(
      e.dataTransfer,
      e.clientX,
      e.clientY,
      rowRef.current,
    )
    setGwWmActiveDrag({
      kind: 'subCompetency',
      competencyId,
      fromIndex: subIndex,
    })
    setIsDragging(true)
  }

  const handleSubDragEnd = () => {
    setGwWmActiveDrag(null)
    onSubDropTargetChange(null)
    setIsDragging(false)
  }

  const handleRowDragOver = (e: React.DragEvent) => {
    if (isReadOnly) return
    const active = getGwWmActiveDrag()
    if (!active) return

    if (active.kind === 'subCompetency' && active.competencyId === competencyId) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (active.fromIndex !== subIndex) {
        onSubDropTargetChange(subIndex)
      }
      return
    }

    if (active.kind === 'competency') {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  const handleRowDrop = (e: React.DragEvent) => {
    if (isReadOnly) return
    const p = getGwWmDragData(e)
    if (!p) return

    if (p.kind === 'subCompetency' && p.competencyId === competencyId) {
      e.preventDefault()
      e.stopPropagation()
      onSubDropTargetChange(null)
      if (p.fromIndex !== subIndex) {
        void persistSubOrder(p.fromIndex, subIndex)
      }
      return
    }

    if (p.kind === 'competency') {
      e.preventDefault()
      e.stopPropagation()
      if (p.fromIndex !== competencyIndex) {
        onCompetencyReorderDrag(p.fromIndex, competencyIndex)
      }
    }
  }

  return (
    <>
      <div
        ref={rowRef}
        className={`gw-wm__sub-row${isDragging ? ' gw-wm__sub-row--dragging' : ''}${isDropTarget ? ' gw-wm__sub-row--drop-target' : ''}`}
        aria-dropeffect={isDropTarget ? 'move' : undefined}
        onDragOver={handleRowDragOver}
        onDrop={handleRowDrop}
      >
        <WheelManagementDragHandle
          enabled={!isReadOnly}
          onDragStart={handleSubDragStart}
          onDragEnd={handleSubDragEnd}
          title="Drag to reorder sub-competency"
          className="gw-wm__drag-handle--sub"
        />
        <div className="gw-wm__sub-row-main">
          <div className="gw-wm__sub-row-text">
            <div className="gw-wm__sub-row-title">
              <h4 className="gw-wm__sub-name">{subCompetency.name}</h4>
              <span
                className={`gw-wm__badge ${
                  isComplete ? 'gw-wm__badge--ok' : 'gw-wm__badge--warn'
                }`}
              >
                {levelStatus.defined}/{levelStatus.total} rubric levels defined
              </span>
            </div>
            <div draggable={false}>
              <RichTextView
                className="gw-wm__sub-desc"
                html={subCompetency.description}
                fallback={
                  <p className="gw-wm__sub-desc gw-wm__desc-placeholder">
                    No description yet.
                  </p>
                }
              />
            </div>
          </div>
        </div>
        {!isReadOnly ? (
          <div className="gw-wm__area-actions">
            <button
              type="button"
              className="gw-wm__btn-icon"
              draggable={false}
              title="Edit sub-competency"
              onClick={startEdit}
            >
              <FontAwesomeIcon icon={faPenToSquare} size="sm" />
            </button>
            <button
              type="button"
              className="gw-wm__btn-icon gw-wm__btn-icon--danger"
              draggable={false}
              title="Delete sub-competency"
              onClick={() => setDeleteModalOpen(true)}
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </button>
          </div>
        ) : null}
      </div>

      {editingSubCompetency && editedSubCompetency && !isReadOnly && (
        <EditSubCompetencyPanel
          editedSubCompetency={editedSubCompetency}
          baselineSubCompetency={editingSubCompetency}
          setEditedSubCompetency={setEditedSubCompetency}
          roleName={roleName}
          parentCompetencyName={parentCompetencyName}
          onClose={closeEditPanel}
          onSave={requestSaveEdit}
        />
      )}

      {saveWarningOpen && saveWarningContent && (
        <WheelManagementActionsConfirmModal
          tone="warning"
          title={saveWarningContent.title}
          confirmLabel={saveWarningContent.confirmLabel}
          onCancel={() => setSaveWarningOpen(false)}
          onConfirm={() => void confirmSaveEdit()}
          irreversibleMessage="This reset cannot be undone."
        >
          <p className="gw-wm__hint">{saveWarningContent.description}</p>
        </WheelManagementActionsConfirmModal>
      )}

      {deleteModalOpen && (
        <WheelManagementActionsConfirmModal
          title="Delete sub-competency"
          confirmLabel="Delete sub-competency"
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => void confirmDelete()}
        >
          <p className="gw-wm__hint">
            Are you sure you want to delete &quot;{subCompetency.name}&quot;? This
            will also delete all levels 1–5 rubric definitions for this
            sub-competency and remove all associated assessment scores and notes.
          </p>
        </WheelManagementActionsConfirmModal>
      )}
    </>
  )
}
